import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FaWallet,
  FaCopy,
  FaCheck,
  FaSync,
  FaArrowUp,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import {
  createUserWallet,
  getUserWallets,
  refreshWalletBalance,
  requestWithdraw,
} from "../services/api";
import { formatBalance } from "../utils/formatters";
import Card from "./ui/Card";
import Button from "./ui/Button";
import WithdrawModal from "./ui/WithdrawModal";

const WalletContainer = styled(motion.div)`
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const WalletCard = styled(Card)`
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colors.background.card},
    ${(props) => props.theme.colors.background.main}
  );
  border: 1px solid ${(props) => props.theme.colors.border};
`;

const WalletHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const WalletAddress = styled.div`
  background: rgba(0, 0, 0, 0.3);
  padding: ${(props) => props.theme.spacing.sm}
    ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  font-family: "Courier New", monospace;
  font-size: ${(props) => props.theme.fontSize.sm};
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.accent};
  cursor: pointer;
  padding: ${(props) => props.theme.spacing.xs};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    background: ${(props) => props.theme.colors.background.light};
  }
`;

const BalanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const BalanceItem = styled.div`
  text-align: center;
  padding: ${(props) => props.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${(props) => props.theme.borderRadius.sm};

  .label {
    font-size: ${(props) => props.theme.fontSize.xs};
    color: ${(props) => props.theme.colors.text.muted};
    margin-bottom: ${(props) => props.theme.spacing.xs};
  }

  .amount {
    font-weight: 600;
    color: ${(props) => props.theme.colors.text.primary};
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${(props) => props.theme.spacing.lg};
`;

const LoadingText = styled.p`
  color: ${(props) => props.theme.colors.text.muted};
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const WithdrawSection = styled.div`
  margin-top: ${(props) => props.theme.spacing.md};
  border-top: 1px solid ${(props) => props.theme.colors.border};
  padding-top: ${(props) => props.theme.spacing.md};
`;

const WithdrawHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: ${(props) => props.theme.spacing.sm};
  border-radius: ${(props) => props.theme.borderRadius.md};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${(props) => props.theme.colors.background.light};
  }
`;

const WithdrawTitle = styled.h4`
  margin: 0;
  color: ${(props) => props.theme.colors.text.primary};
  font-size: ${(props) => props.theme.fontSize.md};
`;

const WithdrawStatus = styled.div`
  padding: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.sm};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid
    ${(props) =>
      props.status === "completed"
        ? props.theme.colors.status.success
        : props.status === "failed"
          ? props.theme.colors.status.error
          : props.theme.colors.border};
  background: ${(props) =>
    props.status === "completed"
      ? "rgba(76, 175, 80, 0.1)"
      : props.status === "failed"
        ? "rgba(255, 94, 91, 0.1)"
        : props.theme.colors.background.card};
`;

const WithdrawStatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing.xs};

  &:last-child {
    margin-bottom: 0;
  }
`;

const StatusBadge = styled.span`
  padding: ${(props) => props.theme.spacing.xs}
    ${(props) => props.theme.spacing.sm};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  font-size: ${(props) => props.theme.fontSize.sm};
  font-weight: 600;
  color: ${(props) =>
    props.status === "completed"
      ? props.theme.colors.status.success
      : props.status === "failed"
        ? props.theme.colors.status.error
        : props.theme.colors.text.muted};
  background: ${(props) =>
    props.status === "completed"
      ? "rgba(76, 175, 80, 0.2)"
      : props.status === "failed"
        ? "rgba(255, 94, 91, 0.2)"
        : "rgba(128, 128, 128, 0.2)"};
  text-transform: uppercase;
`;

const UserWalletManager = ({ wallet, networkId }) => {
  const [userWallets, setUserWallets] = useState([]);
  const [copiedAddress, setCopiedAddress] = useState(null);

  // Withdraw functionality state
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState(null);
  const [withdrawRequests, setWithdrawRequests] = useState([]);
  const [showWithdrawStatus, setShowWithdrawStatus] = useState(false);

  const loadUserWallets = useCallback(async () => {
    if (!wallet?.address || !networkId) {
      if (process.env.NODE_ENV === "development") {
        console.log("Missing wallet address or networkId:", {
          address: wallet?.address,
          networkId,
        });
      }
      return;
    }

    if (process.env.NODE_ENV === "development") {
      console.log(
        "Loading user wallets for:",
        wallet.address,
        "network:",
        networkId,
      );
    }

    try {
      if (process.env.NODE_ENV === "development") {
        console.log("Calling getUserWallets...");
      }

      const response = await getUserWallets(wallet.address, networkId);

      if (process.env.NODE_ENV === "development") {
        console.log("getUserWallets response:", response);
      }

      if (response && response.wallets) {
        // Filter out invalid wallet objects
        const validWallets = (response.wallets || []).filter(
          (w) => w && typeof w === "object",
        );

        if (process.env.NODE_ENV === "development") {
          console.log("Valid wallets after filtering:", validWallets);
          console.log(
            "Filtered out wallets:",
            (response.wallets || []).filter((w) => !w || typeof w !== "object"),
          );
        }

        setUserWallets(validWallets);

        // Check if we have a wallet for the current network
        const currentNetworkWallet = validWallets.find(
          (w) => w.network_id === networkId,
        );

        if (process.env.NODE_ENV === "development") {
          console.log("Current network wallet:", currentNetworkWallet);
        }

        if (!currentNetworkWallet) {
          if (process.env.NODE_ENV === "development") {
            console.log(
              "No wallet found for network, creating automatically...",
            );
          }

          // Create wallet for current network
          try {
            if (process.env.NODE_ENV === "development") {
              console.log("Calling createUserWallet...");
            }

            const createResponse = await createUserWallet(
              wallet.address,
              networkId,
            );

            if (process.env.NODE_ENV === "development") {
              console.log("createUserWallet response:", createResponse);
            }

            if (createResponse && createResponse.user_wallet) {
              if (process.env.NODE_ENV === "development") {
                console.log("Created new wallet:", createResponse.user_wallet);
              }
              setUserWallets((prev) => [...prev, createResponse.user_wallet]);
            }
          } catch (createError) {
            console.error("Error creating user wallet:", createError);

            // Try creating wallet directly
            if (process.env.NODE_ENV === "development") {
              console.log("Attempting to create wallet directly...");
            }

            try {
              const directResponse = await createUserWallet(
                wallet.address,
                networkId,
              );

              if (process.env.NODE_ENV === "development") {
                console.log(
                  "Created new wallet directly:",
                  directResponse.user_wallet,
                );
              }

              if (directResponse && directResponse.user_wallet) {
                setUserWallets((prev) => [...prev, directResponse.user_wallet]);
              }
            } catch (directError) {
              console.error("Error creating wallet directly:", directError);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error loading user wallets:", error);
    }
  }, [wallet?.address, networkId]);

  // Load wallets when wallet connects or network changes
  useEffect(() => {
    if (wallet?.address && networkId) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "UserWalletManager: Wallet connected with auth, loading wallets:",
          { address: wallet.address, networkId },
        );
      }
      // Reset wallets first, then load new ones
      setUserWallets([]);
      loadUserWallets();
    } else {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "UserWalletManager: No proper wallet connection, skipping wallet load",
        );
      }
      // Clear wallets when no connection
      setUserWallets([]);
    }
  }, [wallet?.address, networkId, loadUserWallets]);

  const refreshBalance = async (userWallet) => {
    try {
      const response = await refreshWalletBalance(
        userWallet.trading_address,
        userWallet.network_id,
      );
      if (response.success && response.user_wallet) {
        // Update the wallet in the list
        setUserWallets((prev) =>
          prev
            .filter((w) => w && typeof w === "object")
            .map((w) => (w.id === userWallet.id ? response.user_wallet : w)),
        );
      }
    } catch (error) {
      console.error("Error refreshing balance:", error);
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAddress(id);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleWithdraw = async ({ withdrawAll, tokenSymbol, amount }) => {
    setWithdrawLoading(true);
    setWithdrawError(null);

    try {
      const response = await requestWithdraw(
        wallet.address,
        networkId,
        withdrawAll,
        tokenSymbol,
        amount,
      );

      if (response.success) {
        // Add the new withdraw request to the list
        setWithdrawRequests((prev) => [response.withdraw_info, ...prev]);
        setShowWithdrawModal(false);
        setShowWithdrawStatus(true); // Auto-expand status section

        // Refresh wallet balance after a short delay
        setTimeout(() => {
          if (currentNetworkWallet) {
            refreshBalance(currentNetworkWallet);
          }
        }, 2000);
      } else {
        setWithdrawError(
          response.error || "Failed to process withdraw request",
        );
      }
    } catch (error) {
      console.error("Error processing withdraw:", error);
      setWithdrawError("Failed to process withdraw request");
    } finally {
      setWithdrawLoading(false);
    }
  };

  const handleWithdrawClick = () => {
    setWithdrawError(null);
    setShowWithdrawModal(true);
  };

  const handleCloseWithdrawModal = () => {
    setShowWithdrawModal(false);
    setWithdrawError(null);
  };

  const currentNetworkWallet = userWallets.find(
    (w) => w && w.network_id === networkId,
  );

  if (!wallet) {
    return null;
  }

  return (
    <WalletContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <WalletCard>
        <WalletHeader>
          <h3>
            <FaWallet style={{ marginRight: "8px" }} />
            Trading Wallet
          </h3>
          {currentNetworkWallet && (
            <Button
              size="small"
              onClick={() => refreshBalance(currentNetworkWallet)}
            >
              Refresh
            </Button>
          )}
        </WalletHeader>

        {!currentNetworkWallet ? (
          <LoadingContainer>
            <LoadingText>Creating trading wallet...</LoadingText>
          </LoadingContainer>
        ) : currentNetworkWallet ? (
          <>
            <WalletAddress>
              <span>{currentNetworkWallet.trading_address}</span>
              <CopyButton
                onClick={() =>
                  copyToClipboard(
                    currentNetworkWallet.trading_address,
                    currentNetworkWallet.id,
                  )
                }
                title="Copy address"
              >
                {copiedAddress === currentNetworkWallet.id ? (
                  <FaCheck />
                ) : (
                  <FaCopy />
                )}
              </CopyButton>
            </WalletAddress>

            <BalanceGrid>
              <BalanceItem>
                <div className="label">BNB</div>
                <div className="amount">
                  {formatBalance(currentNetworkWallet.balance_bnb)}
                </div>
              </BalanceItem>
              <BalanceItem>
                <div className="label">GTAI</div>
                <div className="amount">
                  {formatBalance(currentNetworkWallet.balance_gtai)}
                </div>
              </BalanceItem>
              <BalanceItem>
                <div className="label">USDT</div>
                <div className="amount">
                  {formatBalance(currentNetworkWallet.balance_usdt)}
                </div>
              </BalanceItem>
            </BalanceGrid>

            <ButtonGroup>
              <Button
                size="small"
                variant="secondary"
                onClick={() => refreshBalance(currentNetworkWallet)}
                style={{ flex: 1 }}
              >
                <FaSync style={{ marginRight: "8px" }} />
                Refresh Balances
              </Button>
              <Button
                size="small"
                variant="primary"
                onClick={handleWithdrawClick}
                style={{ flex: 1 }}
              >
                <FaArrowUp style={{ marginRight: "8px" }} />
                Withdraw
              </Button>
            </ButtonGroup>

            {/* Withdraw Status Section */}
            <WithdrawSection>
              <WithdrawHeader
                onClick={() => setShowWithdrawStatus(!showWithdrawStatus)}
              >
                <WithdrawTitle>
                  Withdraw Requests ({withdrawRequests.length})
                </WithdrawTitle>
                {showWithdrawStatus ? <FaChevronUp /> : <FaChevronDown />}
              </WithdrawHeader>

              {showWithdrawStatus && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ overflow: "hidden" }}
                >
                  {withdrawRequests.length === 0 ? (
                    <div
                      style={{
                        padding: "16px",
                        textAlign: "center",
                        color: "#666",
                        fontStyle: "italic",
                      }}
                    >
                      No withdraw requests yet
                    </div>
                  ) : (
                    withdrawRequests.map((request) => (
                      <WithdrawStatus key={request.id} status={request.status}>
                        <WithdrawStatusRow>
                          <span>
                            {request.withdraw_all
                              ? "All Tokens"
                              : `${request.amount} ${request.token_symbol}`}
                          </span>
                          <StatusBadge status={request.status}>
                            {request.status}
                          </StatusBadge>
                        </WithdrawStatusRow>
                        <WithdrawStatusRow>
                          <span style={{ fontSize: "12px", color: "#666" }}>
                            {new Date(request.created_at).toLocaleString()}
                          </span>
                          {request.tx_hash && (
                            <span style={{ fontSize: "12px", color: "#666" }}>
                              TX: {request.tx_hash.substring(0, 8)}...
                            </span>
                          )}
                        </WithdrawStatusRow>
                        {request.error_message && (
                          <WithdrawStatusRow>
                            <span
                              style={{ fontSize: "12px", color: "#ff5e5b" }}
                            >
                              {request.error_message}
                            </span>
                          </WithdrawStatusRow>
                        )}
                      </WithdrawStatus>
                    ))
                  )}
                </motion.div>
              )}
            </WithdrawSection>
          </>
        ) : (
          <LoadingContainer>
            <LoadingText>
              No trading wallet found. Please check console for errors.
            </LoadingText>
            <Button
              size="small"
              onClick={() => {
                loadUserWallets();
              }}
              style={{ marginTop: "10px" }}
            >
              Retry
            </Button>
          </LoadingContainer>
        )}
      </WalletCard>

      {/* Withdraw Modal */}
      <WithdrawModal
        isOpen={showWithdrawModal}
        onClose={handleCloseWithdrawModal}
        onWithdraw={handleWithdraw}
        userWallet={currentNetworkWallet}
        networkId={networkId}
        loading={withdrawLoading}
        error={withdrawError}
      />
    </WalletContainer>
  );
};

export default UserWalletManager;
